/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, ReactNode } from "react"
import { Drawer, Button } from "@duxton-web/ui"

import { sortDiffList } from "utils/misc"
import { useTranslation } from "common/utils/i18n"
import { useSelector } from "react-redux"

import { AppState } from "store/store.type"
import {
  SortableItemList,
  ItemListProps,
} from "components/common/SortableItemList"

import { drawerFooterStyle } from "../styles"

const drawerWidth = 450

interface SortDrawerProps extends ItemListProps {
  title?: string
  visible: boolean
  onSave?(sortedItems: any[]): void
  onClose(): void
  onSort?({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }): void
  onClear?(): void
  renderRowContent(item: any): ReactNode
}

export const SortDrawer: FC<SortDrawerProps> = ({
  title,
  visible,
  onSave,
  onClose,
  onSort,
  onClear,
  renderRowContent,
  ...rest
}: SortDrawerProps) => {
  const { isEditAllowed } = useSelector((state: AppState) => state.menu)

  const textSave = useTranslation("menu.buttons.saveChanges")

  return (
    <Drawer
      destroyOnClose
      title={title ?? "Rearrange Items"}
      width={drawerWidth}
      open={visible}
      onClose={onClose}
    >
      <SortableItemList
        {...rest}
        draggable
        backgroundColor="rgba(0, 0, 0, 0.15)"
        renderRowContent={item => (
          <div
            css={css`
              padding: 0 23px 0 0;
            `}
          >
            {renderRowContent(item)}
          </div>
        )}
        onSort={onSort}
        onClear={onClear}
        renderFooter={({ oldList, newList }) => (
          <div css={drawerFooterStyle}>
            <Button
              data-testid="save-button"
              type="primary"
              block
              onClick={() => {
                onSave?.(newList)
                onClose?.()
              }}
              disabled={
                !sortDiffList({
                  oldList,
                  newList,
                }).length || !isEditAllowed
              }
            >
              {textSave}
            </Button>
          </div>
        )}
      />
    </Drawer>
  )
}
